import React, { useState, useEffect, useRef } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '../lib/utils';

const thinkingMessages = {
  analyzing: [
    "analyzing patterns",
    "connecting the dots",
    "synthesizing insights",
    "parsing the signal",
    "decoding complexity",
    "mapping relationships",
    "weighing the evidence",
    "cross-referencing data",
  ],
  cooking: [
    "cooking up results",
    "simmering thoughts",
    "brewing insights",
    "marinating ideas",
    "seasoning the data",
    "letting it reduce",
  ],
  fun: [
    "discombobulating",
    "recalibrating flux capacitors",
    "consulting the oracle",
    "reading tea leaves",
    "channeling collective wisdom",
    "untangling spaghetti",
    "herding cats",
    "aligning chakras",
    "communing with the cloud",
    "asking the magic 8-ball",
    "polishing pixels",
    "wrangling electrons",
    "tickling the database",
    "negotiating with APIs",
    "summoning insights",
  ],
  scanning: [
    "scanning the horizon",
    "sweeping for updates",
    "combing through changes",
    "surveying the landscape",
    "gathering intelligence",
    "sifting through signals",
    "tracking movements",
  ],
};

// Get messages for a category or mix all categories
const getMessages = (category) => {
  if (category === 'mixed') {
    return Object.values(thinkingMessages).flat();
  }
  return thinkingMessages[category] || thinkingMessages.fun;
};

// Shuffle array using Fisher-Yates algorithm
const shuffleArray = (array) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

export function ThinkingIndicator({
  category = 'mixed',
  className,
  interval = 2500,
  showIcon = true,
  size = 'default', // 'default' | 'sm' | 'lg'
}) {
  const [currentMessage, setCurrentMessage] = useState('');
  const [isTransitioning, setIsTransitioning] = useState(false);
  const messagesRef = useRef([]);
  const indexRef = useRef(0);

  // Initialize and shuffle messages
  useEffect(() => {
    messagesRef.current = shuffleArray(getMessages(category));
    indexRef.current = 0;
    setCurrentMessage(messagesRef.current[0]);
  }, [category]);

  // Cycle through messages
  useEffect(() => {
    const timer = setInterval(() => {
      setIsTransitioning(true);

      setTimeout(() => {
        indexRef.current = (indexRef.current + 1) % messagesRef.current.length;

        // Reshuffle when we've gone through all messages
        if (indexRef.current === 0) {
          messagesRef.current = shuffleArray(getMessages(category));
        }

        setCurrentMessage(messagesRef.current[indexRef.current]);
        setIsTransitioning(false);
      }, 150);
    }, interval);

    return () => clearInterval(timer);
  }, [category, interval]);

  const sizeClasses = {
    sm: 'text-xs',
    default: 'text-sm',
    lg: 'text-base',
  };

  const iconSizeClasses = {
    sm: 'h-3 w-3',
    default: 'h-4 w-4',
    lg: 'h-5 w-5',
  };

  return (
    <div
      className={cn(
        "flex items-center gap-2 text-muted-foreground",
        sizeClasses[size],
        className
      )}
    >
      {showIcon && (
        <Loader2 className={cn("animate-spin", iconSizeClasses[size])} />
      )}
      <span
        className={cn(
          "transition-opacity duration-150",
          isTransitioning ? "opacity-0" : "opacity-100"
        )}
      >
        ...{currentMessage}
      </span>
    </div>
  );
}

export default ThinkingIndicator;
